import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, Trash2, FileText } from "lucide-react";

const MonthCard = ({ m, selected, onClick }) => (
  <button onClick={onClick} data-testid={`adinv-month-${m.month}`}
    className={`text-left bg-card border rounded-lg p-4 shadow-sm transition-colors ${selected ? "border-blue-600 ring-1 ring-blue-600" : "border-border hover:border-blue-400"}`}>
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{m.month}</p>
    <p className="text-xl font-semibold mt-1">{inr(m.total)}</p>
    <p className="text-[11px] text-muted-foreground mt-0.5">{m.invoices} invoice{m.invoices > 1 ? "s" : ""} · tax {inr(m.tax)}</p>
  </button>
);

export const AdInvoices = () => {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selMonth, setSelMonth] = useState(null);
  const fileRef = useRef();

  const load = () => api.get("/tools/ad-invoices").then(({ data }) => {
    setData(data);
    if (data.monthly.length && !selMonth) setSelMonth(data.monthly[0].month);
  });
  useEffect(() => { load(); }, []); // eslint-disable-line

  const upload = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      const { data: res } = await api.post("/tools/ad-invoices/upload", fd, { timeout: 180000 });
      res.results.forEach((r) => {
        if (r.status === "parsed") toast.success(`${r.filename}: parsed — ${r.month}, ${inr(r.total_amount)} (${r.line_items} categories)`);
        else if (r.status === "duplicate") toast.info(`${r.filename}: ${r.detail}`);
        else toast.error(`${r.filename}: ${r.detail}`);
      });
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    await api.delete(`/tools/ad-invoices/${encodeURIComponent(inv.invoice_number)}`);
    toast.success("Invoice deleted");
    load();
  };

  const download = async (inv) => {
    const res = await api.get(`/tools/ad-invoices/${encodeURIComponent(inv.invoice_number)}/file`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = inv.filename || `${inv.invoice_number}.pdf`;
    a.click();
  };

  if (!data) return <Skeleton className="h-72" />;
  const monthData = data.monthly.find((m) => m.month === selMonth);

  return (
    <div className="space-y-6" data-testid="ad-invoices-tool">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Upload your agency's Amazon Advertising invoices (PDF). They're parsed automatically — invoice number, billing month,
          totals and the category-wise ad spend — and accounted in the <strong>MIS Report</strong> (as "External Ad Spend") and <strong>Monthly P&L</strong>.
        </p>
        <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden"
          onChange={(e) => upload(e.target.files)} data-testid="adinv-file-input" />
        <Button className="rounded-full" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="adinv-upload-button">
          <Upload className="h-4 w-4 mr-1.5" aria-hidden="true" /> {uploading ? "Parsing…" : "Upload invoices"}
        </Button>
      </div>

      {data.monthly.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center text-sm text-muted-foreground" data-testid="adinv-empty">
          <FileText className="h-8 w-8 mx-auto mb-3 opacity-40" aria-hidden="true" />
          No invoices yet — upload your first agency invoice PDF above.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="adinv-month-cards">
            {data.monthly.map((m) => (
              <MonthCard key={m.month} m={m} selected={m.month === selMonth} onClick={() => setSelMonth(m.month)} />
            ))}
          </div>

          {monthData && (
            <div className="bg-card border border-border rounded-lg shadow-sm p-5" data-testid="adinv-category-breakdown">
              <h3 className="font-heading text-base font-semibold tracking-tight mb-3">
                {monthData.month} — ad spend by category ({inr(monthData.total)} total)
              </h3>
              <div className="grid md:grid-cols-2 gap-x-8">
                {monthData.categories.map((c) => (
                  <div key={c.category} className="flex items-center justify-between py-1.5 border-b border-border/60 text-sm" data-testid={`adinv-cat-${c.category}`}>
                    <span>{c.category}</span>
                    <span className="font-medium">{inr(c.amount)}
                      <span className="text-[11px] text-muted-foreground ml-2">{monthData.total ? ((c.amount / monthData.total) * 100).toFixed(1) : 0}%</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-card border border-border rounded-lg shadow-sm overflow-x-auto">
            <table className="w-full text-sm" data-testid="adinv-table">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>{["Month", "Invoice #", "Invoice Date", "Period", "Taxable", "Tax", "Total", "Uploaded", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.invoice_number} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`adinv-row-${inv.invoice_number}`}>
                    <td className="px-4 py-2.5"><Badge variant="secondary">{inv.month}</Badge></td>
                    <td className="px-4 py-2.5 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="px-4 py-2.5">{inv.invoice_date}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{inv.period_start} → {inv.period_end}</td>
                    <td className="px-4 py-2.5">{inr(inv.taxable_amount)}</td>
                    <td className="px-4 py-2.5">{inr(inv.tax_amount)}</td>
                    <td className="px-4 py-2.5 font-semibold">{inr(inv.total_amount)}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{inv.uploaded_by}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => download(inv)} data-testid={`adinv-download-${inv.invoice_number}`}>
                          <Download className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => remove(inv)} data-testid={`adinv-delete-${inv.invoice_number}`}>
                          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
